import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  ConfigProvider,
  Popconfirm,
  Space,
  Switch,
  InputNumber,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import "./aiMessageModes.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import NotificationBell from "../../components/notificationBell/notificationBell";

const { TextArea } = Input;

const AiMessageModes = () => {
  const [modes, setModes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMode, setEditingMode] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    getModes();
  }, []);

  const getModes = () => {
    const onSuccess = (res) => {
      if (res.status === 200) {
        const modesData = res.data?.data?.data || res.data?.data || res.data || [];
        setModes(Array.isArray(modesData) ? modesData : []);
      }
    };

    callApi(
      "GET",
      routes.getAllAiMessageModes,
      null,
      setIsLoading,
      onSuccess,
      (error) => {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to load AI message modes";
        message.error(errorMsg);
      }
    );
  };

  const handleCreate = () => {
    setEditingMode(null);
    form.setFieldsValue({
      emoji: "",
      isActive: true,
      sortOrder: 1,
    });
    setIsModalVisible(true);
  };

  const handleEdit = (mode) => {
    setEditingMode(mode);
    form.setFieldsValue({
      name: mode.name,
      description: mode.description,
      emoji: mode.emoji || "",
      isActive: mode.isActive,
      sortOrder: mode.sortOrder ?? 1,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (modeId) => {
    const onSuccess = (res) => {
      if (res.status === 200 || res.status === 201) {
        message.success("Mode deleted successfully");
      } else {
        message.warning(`Delete completed with status ${res.status}`);
      }
      getModes();
    };

    callApi(
      "DELETE",
      `${routes.deleteAiMessageMode}/${modeId}`,
      null,
      setIsLoading,
      onSuccess,
      (error) => {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to delete mode";
        message.error(errorMsg);
      }
    );
  };

  const handleSubmit = (values) => {
    const currentEditingMode = editingMode;
    setIsModalVisible(false);
    form.resetFields();
    setEditingMode(null);

    const onSuccess = (res) => {
      if (res.status === 200 || res.status === 201) {
        message.success(currentEditingMode ? "Mode updated successfully" : "Mode created successfully");
      } else {
        message.warning(`Request completed with status ${res.status}`);
      }
      getModes();
    };

    if (currentEditingMode) {
      callApi(
        "PATCH",
        `${routes.updateAiMessageMode}/${currentEditingMode._id}`,
        values,
        setIsLoading,
        onSuccess,
        (error) => {
          const errorMsg = error?.response?.data?.message || error?.message || "Failed to update mode";
          message.error(errorMsg);
        }
      );
    } else {
      callApi(
        "POST",
        routes.createAiMessageMode,
        values,
        setIsLoading,
        onSuccess,
        (error) => {
          const errorMsg = error?.response?.data?.message || error?.message || "Failed to create mode";
          message.error(errorMsg);
        }
      );
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingMode(null);
  };

  const filteredModes = modes.filter((mode) => {
    const q = searchText.toLowerCase();
    return (
      mode.name?.toLowerCase().includes(q) ||
      mode.description?.toLowerCase().includes(q) ||
      mode.emoji?.toLowerCase().includes(q)
    );
  });

  const columns = [
    {
      title: "No",
      key: "index",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Emoji",
      dataIndex: "emoji",
      key: "emoji",
      width: 90,
      render: (emoji) => <span style={{ fontSize: 20 }}>{emoji || "-"}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 500 }}>{text || "N/A"}</span>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <span style={{ color: "#666" }}>
          {text && text.length > 120 ? `${text.substring(0, 120)}...` : text || "N/A"}
        </span>
      ),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (active) => (
        <Tag color={active ? "green" : "red"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Sort Order",
      dataIndex: "sortOrder",
      key: "sortOrder",
      width: 100,
      render: (value) => value ?? "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Mode"
            description="Are you sure you want to delete this mode?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FF66B2",
        },
      }}
    >
      <div className="ai-modes-container">
        <Loader loading={isLoading} />

        <div className="ai-modes-header">
          <h1 className="ai-modes-title">AI Message Modes</h1>
          <NotificationBell />
        </div>

        <div className="ai-modes-content">
          <Card className="ai-modes-card">
            <div className="ai-modes-actions">
              <Input
                placeholder="Search modes by name or description..."
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => setSearchText(e.target.value)}
                style={{ maxWidth: 340 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Add Mode
              </Button>
            </div>

            <Table
              columns={columns}
              dataSource={filteredModes}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total) => `Total ${total} modes`,
              }}
            />
          </Card>
        </div>

        <Modal
          title={editingMode ? "Edit AI Message Mode" : "Add AI Message Mode"}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={650}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: "Please enter mode name" },
                { min: 3, message: "Name must be at least 3 characters" },
              ]}
            >
              <Input placeholder="e.g. Playful and flirty" prefix={<RobotOutlined />} />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              rules={[
                { required: true, message: "Please enter description" },
                { min: 10, message: "Description must be at least 10 characters" },
              ]}
            >
              <TextArea
                rows={5}
                placeholder="Description controls AI tone and behavior..."
                showCount
                maxLength={1000}
              />
            </Form.Item>

            <Form.Item
              label="Emoji"
              name="emoji"
              rules={[
                { max: 20, message: "Emoji field cannot exceed 20 characters" },
              ]}
            >
              <Input placeholder="e.g. 😍" />
            </Form.Item>

            <div className="ai-modes-form-row">
              <Form.Item
                label="Active"
                name="isActive"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label="Sort Order"
                name="sortOrder"
                initialValue={1}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </div>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingMode ? "Update" : "Create"}
                </Button>
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

      </div>
    </ConfigProvider>
  );
};

export default AiMessageModes;

